import {
    ArrowDownTrayIcon,
    GlobeAltIcon,
    KeyIcon,
    PencilSquareIcon,
    SparklesIcon,
} from "@heroicons/react/24/outline";
import {
    addDoc,
    collection,
    doc,
    getDocs,
    setDoc,
} from "firebase/firestore";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStatus } from "../components/hooks/useAuthStatus";
import {
    callAI,
    kAIProviderStorage,
    keyStorageFor,
    modelStorageFor,
    providerConfig,
} from "../components/utils/aiProviders";
import { useLanguage } from "../context/Language/LanguageContext";
import { db } from "../firebase.config";
import {
    LANGUAGES,
    TRANSLATION_SECTIONS,
    translations,
} from "../i18n/translations";

// ─── helpers ─────────────────────────────────────────────────────────────────

function getLangDisplay(code) {
  return LANGUAGES[code]
    ? `${LANGUAGES[code].flag} ${LANGUAGES[code].label}`
    : code;
}

// Loads the AI provider/key/model the user already configured for AI reports.
function loadAISettings() {
  const provider = localStorage.getItem(kAIProviderStorage) || "openai";
  const apiKey = localStorage.getItem(keyStorageFor(provider)) || "";
  const model =
    localStorage.getItem(modelStorageFor(provider)) ||
    providerConfig(provider).models[0];
  return { provider, apiKey, model };
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function Translation({ onClose }) {
  const { firebaseUser } = useAuthStatus();
  const { registerLanguage, syncCustomLanguages, t } = useLanguage();
  const navigate = useNavigate();
  const handleClose = onClose ? onClose : () => navigate(-1);

  // ── Phase: "setup" | "editor" ──────────────────────────────────────────────
  const [phase, setPhase] = useState("setup");

  // ── Setup form state ──────────────────────────────────────────────────────
  const [langName, setLangName] = useState("");
  const [langCode, setLangCode] = useState("");
  const [langFlag, setLangFlag] = useState("");
  const [baseLang, setBaseLang] = useState("en");

  // ── Shared languages (import) ─────────────────────────────────────────────
  const [sharedLangs, setSharedLangs] = useState([]);
  const [ownPackages, setOwnPackages] = useState([]);
  const [loadingShared, setLoadingShared] = useState(false);

  // ── AI provider/key/model (reuses the AI report settings) ──────────────────
  const initialAI = loadAISettings();
  const [aiKey, setAiKey] = useState(initialAI.apiKey);
  const [aiProvider] = useState(initialAI.provider);
  const [aiModel] = useState(initialAI.model);
  const [showKeyInput, setShowKeyInput] = useState(false);

  // ── Editor state ──────────────────────────────────────────────────────────
  const [values, setValues] = useState({});
  const [aiLoading, setAiLoading] = useState(false);
  const [aiProgress, setAiProgress] = useState("");
  const [saving, setSaving] = useState(false);
  const [sharePrompt, setSharePrompt] = useState(false);
  const [newKeys, setNewKeys] = useState(new Set());
  const [importedFrom, setImportedFrom] = useState(null);
  const [usingLang, setUsingLang] = useState(null);

  // Collect all keys flat for Excel-like navigation.
  const allKeys = TRANSLATION_SECTIONS.flatMap((s) => s.keys);
  const inputRefs = useRef({});

  // ── Load shared + own languages ──────────────────────────────────────────
  const loadLanguages = useCallback(async () => {
    setLoadingShared(true);
    try {
      const sharedSnap = await getDocs(collection(db, "sharedLanguages"));
      const shared = [];
      sharedSnap.forEach((d) => shared.push({ id: d.id, ...d.data() }));
      setSharedLangs(shared);

      if (firebaseUser?.uid) {
        const ownSnap = await getDocs(
          collection(db, "userLanguages", firebaseUser.uid, "packages")
        );
        const own = [];
        ownSnap.forEach((d) => own.push({ id: d.id, ...d.data() }));
        setOwnPackages(own);
        syncCustomLanguages(own);
      } else {
        syncCustomLanguages([]);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingShared(false);
    }
  }, [firebaseUser?.uid, syncCustomLanguages]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadLanguages();
  }, [loadLanguages]);

  // ── Save AI key (into the provider-specific storage) ──────────────────────
  const saveApiKey = () => {
    localStorage.setItem(keyStorageFor(aiProvider), aiKey);
    setShowKeyInput(false);
  };

  // ── Setup submit ─────────────────────────────────────────────────────────
  const handleSetupSubmit = (e) => {
    e.preventDefault();
    if (!langName.trim() || !langCode.trim()) {
      return;
    }
    const preValues = {};
    if (baseLang !== "en" && translations[baseLang]) {
      allKeys.forEach((key) => {
        preValues[key] = translations[baseLang][key] ?? "";
      });
    }
    setNewKeys(new Set());
    setImportedFrom(null);
    setValues(preValues);
    setPhase("editor");
  };

  // ── Import a shared/own language for editing ──────────────────────────────
  const importSharedLang = (shared) => {
    setLangName(shared.langName ?? shared.id);
    setLangCode(shared.langCode ?? shared.id);
    setLangFlag(shared.flag ?? "");
    setBaseLang("en");
    const savedTranslations = shared.translations ?? {};
    const imported = {};
    allKeys.forEach((key) => {
      imported[key] = savedTranslations[key] ?? "";
    });
    setNewKeys(new Set(allKeys.filter((k) => !(k in savedTranslations))));
    setValues(imported);
    setImportedFrom(
      shared.ownerUid && shared.ownerUid !== firebaseUser?.uid
        ? {
            ownerUid: shared.ownerUid,
            ownerName: shared.ownerName,
            docId: shared.id,
          }
        : null
    );
    // Register immediately so it appears in the language picker right away.
    registerLanguage({
      langCode: shared.langCode ?? shared.id,
      langName: shared.langName ?? shared.id,
      flag: shared.flag ?? "🌐",
      translations: shared.translations ?? {},
    });
    setPhase("editor");
  };

  // ── Cell navigation (Excel-like) ──────────────────────────────────────────
  const handleKeyDown = useCallback(
    (e, key) => {
      const idx = allKeys.indexOf(key);
      if (e.key === "Tab") {
        e.preventDefault();
        const next = e.shiftKey ? allKeys[idx - 1] : allKeys[idx + 1];
        if (next) inputRefs.current[next]?.focus();
      } else if (e.key === "ArrowDown" || e.key === "Enter") {
        e.preventDefault();
        const next = allKeys[idx + 1];
        if (next) inputRefs.current[next]?.focus();
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        const prev = allKeys[idx - 1];
        if (prev) inputRefs.current[prev]?.focus();
      }
    },
    [allKeys]
  );

  // ── AI translate (uses the shared callAI helper) ──────────────────────────
  const translateWithAI = async (keysToTranslate) => {
    const key = aiKey.trim();
    if (!key) {
      setShowKeyInput(true);
      return;
    }
    setAiLoading(true);
    const BATCH = 30;
    const batches = [];
    for (let i = 0; i < keysToTranslate.length; i += BATCH) {
      batches.push(keysToTranslate.slice(i, i + BATCH));
    }
    try {
      for (let b = 0; b < batches.length; b++) {
        setAiProgress(`Translating batch ${b + 1} / ${batches.length}…`);
        const batch = batches[b];
        const sourceTexts = batch.map((k) =>
          baseLang === "en" ? k : translations[baseLang]?.[k] ?? k
        );
        const prompt = [
          `Translate each of the following UI strings into ${langName} (language code: ${langCode}).`,
          "Return ONLY a JSON array of translated strings in the same order.",
          "Do not add explanations.",
          "",
          JSON.stringify(sourceTexts),
        ].join("\n");

        const raw = await callAI({
          provider: aiProvider,
          apiKey: key,
          model: aiModel,
          messages: [{ role: "user", content: prompt }],
        });

        let translated;
        try {
          const stripped = (raw || "[]").replace(/```json|```/g, "").trim();
          translated = JSON.parse(stripped);
        } catch {
          translated = [];
        }
        setValues((prev) => {
          const next = { ...prev };
          batch.forEach((k, i) => {
            if (translated[i]) next[k] = translated[i];
          });
          return next;
        });
      }
    } catch (err) {
      alert(`AI translation error: ${err.message}`);
    } finally {
      setAiLoading(false);
      setAiProgress("");
    }
  };

  const handleTranslateAll = () => translateWithAI(allKeys);
  const handleTranslateSection = (keys) => translateWithAI(keys);

  const handleClearAll = () => {
    if (window.confirm(t("Clear all translations? This cannot be undone."))) {
      setValues({});
    }
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async (share) => {
    setSaving(true);
    try {
      const pkg = {
        langCode: langCode.trim(),
        langName: langName.trim(),
        flag: langFlag.trim(),
        translations: values,
        updatedAt: new Date().toISOString(),
      };
      if (firebaseUser?.uid) {
        await setDoc(
          doc(
            db,
            "userLanguages",
            firebaseUser.uid,
            "packages",
            langCode.trim()
          ),
          pkg
        );
      } else {
        localStorage.setItem(`vblive-lang-pkg-${langCode}`, JSON.stringify(pkg));
      }
      if (share) {
        const sharedDoc = {
          ...pkg,
          ownerUid: firebaseUser?.uid ?? "anonymous",
          ownerName:
            firebaseUser?.displayName || firebaseUser?.email || "Anonymous",
          createdAt: new Date().toISOString(),
        };
        if (importedFrom) {
          sharedDoc.basedOnOwnerUid = importedFrom.ownerUid;
          sharedDoc.basedOnOwnerName = importedFrom.ownerName;
          sharedDoc.basedOnDocId = importedFrom.docId;
        }
        await addDoc(collection(db, "sharedLanguages"), sharedDoc);
        alert(
          t("Language package saved and shared with all VBLive users!")
        );
      } else {
        alert(t("Language package saved!"));
      }
      // Register so it appears in the language picker immediately.
      registerLanguage(pkg);
      setSharePrompt(false);
      await loadLanguages();
    } catch (err) {
      alert(`Save error: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveClick = () => setSharePrompt(true);

  // ── Apply a shared language without editing ────────────────────────────────
  const applySharedLang = async (shared) => {
    setUsingLang(shared.id);
    try {
      const pkg = {
        langCode: shared.langCode ?? shared.id,
        langName: shared.langName ?? shared.id,
        flag: shared.flag ?? "🌐",
        translations: shared.translations ?? {},
        updatedAt: new Date().toISOString(),
      };
      if (firebaseUser?.uid) {
        await setDoc(
          doc(db, "userLanguages", firebaseUser.uid, "packages", pkg.langCode),
          pkg
        );
      } else {
        localStorage.setItem(
          `vblive-lang-pkg-${pkg.langCode}`,
          JSON.stringify(pkg)
        );
      }
      registerLanguage(pkg);
      await loadLanguages();
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setUsingLang(null);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER — Setup phase
  // ─────────────────────────────────────────────────────────────────────────

  if (phase === "setup") {
    return (
      <div className="bg-base-100 p-4 sm:p-6">
        <div className="max-w-xl mx-auto">
          <div className="flex items-center gap-2 mb-6">
            <GlobeAltIcon className="size-7 text-primary" />
            <h1 className="text-2xl font-bold flex-1">
              {t("Translation Editor")}
            </h1>
            <button className="btn btn-sm btn-ghost" onClick={handleClose}>
              {t("Close")}
            </button>
          </div>

          {/* Own saved packages */}
          {ownPackages.length > 0 && (
            <div className="mb-6">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-base-content/50 mb-2">
                {t("My saved packages")}
              </h2>
              <div className="flex flex-wrap gap-2">
                {ownPackages.map((pkg) => (
                  <button
                    key={pkg.id}
                    className="btn btn-sm btn-primary btn-outline"
                    onClick={() => importSharedLang(pkg)}
                  >
                    <PencilSquareIcon className="size-4 mr-1" />
                    {pkg.flag} {pkg.langName ?? pkg.id}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Import shared */}
          {loadingShared ? (
            <div className="flex items-center gap-2 mb-4 text-sm text-base-content/50">
              <span className="loading loading-spinner loading-sm" />
              {t("Loading shared languages…")}
            </div>
          ) : sharedLangs.filter((sl) => sl.ownerUid !== firebaseUser?.uid)
              .length > 0 ? (
            <div className="mb-6">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-base-content/50 mb-2">
                {t("Community shared languages")}
              </h2>
              <div className="flex flex-wrap gap-2">
                {sharedLangs
                  .filter((sl) => sl.ownerUid !== firebaseUser?.uid)
                  .map((sl) => (
                    <div
                      key={sl.id}
                      className="flex flex-col gap-1 border border-base-300 rounded-lg px-3 py-2 text-sm bg-base-100"
                    >
                      <span className="flex items-center gap-1 font-medium">
                        {sl.flag} {sl.langName ?? sl.id}
                      </span>
                      {sl.ownerName && (
                        <span className="text-xs opacity-60">
                          {t("by")} {sl.ownerName}
                          {sl.basedOnOwnerName && (
                            <>
                              , {t("based on")} {sl.basedOnOwnerName}
                            </>
                          )}
                        </span>
                      )}
                      <div className="flex gap-1 mt-1">
                        <button
                          className="btn btn-xs btn-primary"
                          onClick={() => applySharedLang(sl)}
                          disabled={usingLang === sl.id}
                        >
                          {usingLang === sl.id ? (
                            <span className="loading loading-spinner loading-xs" />
                          ) : (
                            t("Use")
                          )}
                        </button>
                        <button
                          className="btn btn-xs btn-ghost"
                          onClick={() => importSharedLang(sl)}
                          disabled={!!usingLang}
                        >
                          {t("Edit")}
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
              <div className="divider">{t("or create new")}</div>
            </div>
          ) : null}

          {/* Setup form */}
          <form onSubmit={handleSetupSubmit} className="card bg-base-200 shadow">
            <div className="card-body gap-4 p-6">
              <h2 className="card-title">{t("New Language Package")}</h2>

              <label className="flex flex-col gap-1">
                <span className="label-text font-medium">
                  {t("Language Name")}
                </span>
                <input
                  type="text"
                  className="input input-bordered w-full"
                  placeholder="e.g. Deutsch"
                  value={langName}
                  onChange={(e) => setLangName(e.target.value)}
                  required
                />
              </label>

              <label className="flex flex-col gap-1">
                <span className="label-text font-medium">
                  {t("Language Code")}
                </span>
                <input
                  type="text"
                  className="input input-bordered w-full"
                  placeholder="e.g. de"
                  value={langCode}
                  onChange={(e) =>
                    setLangCode(
                      e.target.value.toLowerCase().replace(/[^a-z-]/g, "")
                    )
                  }
                  required
                />
              </label>

              <label className="flex flex-col gap-1">
                <span className="label-text font-medium">
                  {t("Flag Emoji")}
                </span>
                <input
                  type="text"
                  className="input input-bordered w-full"
                  placeholder="e.g. 🇩🇪"
                  value={langFlag}
                  onChange={(e) => setLangFlag(e.target.value)}
                />
              </label>

              <label className="flex flex-col gap-1">
                <span className="label-text font-medium">
                  {t("Base language (reference)")}
                </span>
                <select
                  className="select select-bordered w-full"
                  value={baseLang}
                  onChange={(e) => setBaseLang(e.target.value)}
                >
                  <option value="en">🇬🇧 English</option>
                  {Object.entries(LANGUAGES)
                    .filter(([code]) => code !== "en")
                    .map(([code, { label, flag }]) => (
                      <option key={code} value={code}>
                        {flag} {label}
                      </option>
                    ))}
                </select>
                <span className="text-xs text-base-content/50">
                  {t(
                    "If not English, both English and the reference language will be shown."
                  )}
                </span>
              </label>

              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={handleClose}
                >
                  {t("Cancel")}
                </button>
                <button type="submit" className="btn btn-primary">
                  {t("Start Editing")}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER — Editor phase
  // ─────────────────────────────────────────────────────────────────────────

  const hasThreeCols = baseLang !== "en";

  return (
    <div className="bg-base-100 p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <button
          className="btn btn-sm btn-ghost"
          onClick={() => setPhase("setup")}
        >
          {t("← Back")}
        </button>
        <GlobeAltIcon className="size-6 text-primary" />
        <h1 className="text-xl font-bold flex-1 flex items-center gap-2 flex-wrap">
          {langFlag} {langName}{" "}
          <span className="text-base-content/40 font-normal text-sm">
            ({langCode})
          </span>
          {newKeys.size > 0 && (
            <span className="badge badge-warning badge-sm font-normal">
              {newKeys.size} {t("new")}
            </span>
          )}
        </h1>

        {/* AI Key toggle */}
        <div className="flex items-center gap-2">
          {showKeyInput ? (
            <div className="flex gap-1">
              <input
                type="password"
                className="input input-bordered input-sm w-56"
                placeholder={providerConfig(aiProvider).keyPlaceholder}
                value={aiKey}
                onChange={(e) => setAiKey(e.target.value)}
              />
              <button className="btn btn-sm btn-primary" onClick={saveApiKey}>
                {t("Save Key")}
              </button>
              <button
                className="btn btn-sm btn-ghost"
                onClick={() => setShowKeyInput(false)}
              >
                {t("Cancel")}
              </button>
            </div>
          ) : (
            <button
              className="btn btn-sm btn-ghost tooltip"
              data-tip={
                aiKey
                  ? t("AI key set")
                  : t("Set API key for AI translation")
              }
              onClick={() => setShowKeyInput(true)}
            >
              <KeyIcon className="size-4" />
              {aiKey ? (
                <span className="text-xs text-success">{t("AI key ✓")}</span>
              ) : (
                <span className="text-xs text-warning">{t("Set AI key")}</span>
              )}
            </button>
          )}
          <button
            className="btn btn-sm btn-secondary"
            onClick={handleTranslateAll}
            disabled={aiLoading}
          >
            <SparklesIcon className="size-4 mr-1" />
            {aiLoading
              ? aiProgress || t("Translating…")
              : t("AI: Translate All")}
          </button>
          <button
            className="btn btn-sm btn-ghost text-error"
            onClick={handleClearAll}
            disabled={aiLoading || saving}
          >
            {t("Clear All")}
          </button>
        </div>

        <button
          className="btn btn-sm btn-primary"
          onClick={handleSaveClick}
          disabled={saving}
        >
          <ArrowDownTrayIcon className="size-4 mr-1" />
          {saving ? t("Saving…") : t("Save")}
        </button>
      </div>

      {/* Column headers */}
      <div
        className={`grid gap-1 text-xs font-semibold uppercase tracking-wide text-base-content/50 px-2 mb-1 ${
          hasThreeCols ? "grid-cols-[2fr_2fr_3fr]" : "grid-cols-[1fr_2fr]"
        }`}
      >
        <span>{t("English")}</span>
        {hasThreeCols && <span>{getLangDisplay(baseLang)}</span>}
        <span>
          {langFlag} {langName}
        </span>
      </div>

      {/* Sections */}
      <div className="space-y-4">
        {TRANSLATION_SECTIONS.map((sec) => (
          <div
            key={sec.section}
            className="card bg-base-200 shadow-sm overflow-hidden"
          >
            <div className="flex items-center justify-between px-4 py-2 bg-base-300">
              <span className="font-semibold text-sm flex items-center gap-2">
                {sec.section}
                {sec.keys.some((k) => newKeys.has(k)) && (
                  <span className="badge badge-warning badge-xs">
                    {sec.keys.filter((k) => newKeys.has(k)).length} {t("new")}
                  </span>
                )}
              </span>
              <button
                className="btn btn-xs btn-ghost"
                onClick={() => handleTranslateSection(sec.keys)}
                disabled={aiLoading}
              >
                <SparklesIcon className="size-3 mr-1" />
                {t("AI translate section")}
              </button>
            </div>
            <div className="divide-y divide-base-300">
              {sec.keys.map((key) => (
                <div
                  key={key}
                  className={`grid gap-1 px-2 py-1 items-center ${
                    hasThreeCols
                      ? "grid-cols-[2fr_2fr_3fr]"
                      : "grid-cols-[1fr_2fr]"
                  }`}
                >
                  <span className="text-sm break-words pr-2 flex items-start gap-1 flex-wrap">
                    <span
                      className={
                        newKeys.has(key)
                          ? "text-warning"
                          : "text-base-content/70"
                      }
                    >
                      {key}
                    </span>
                    {newKeys.has(key) && (
                      <span className="badge badge-warning badge-xs shrink-0">
                        {t("New")}
                      </span>
                    )}
                  </span>
                  {hasThreeCols && (
                    <span className="text-sm text-base-content/60 break-words pr-2">
                      {translations[baseLang]?.[key] ?? ""}
                    </span>
                  )}
                  <input
                    ref={(el) => {
                      inputRefs.current[key] = el;
                    }}
                    type="text"
                    className="input input-bordered input-sm w-full text-sm"
                    value={values[key] ?? ""}
                    onChange={(e) =>
                      setValues((prev) => ({ ...prev, [key]: e.target.value }))
                    }
                    onKeyDown={(e) => handleKeyDown(e, key)}
                    placeholder={t("Enter translation…")}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Share prompt modal */}
      {sharePrompt && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">
              {t("Share with other VBLive users?")}
            </h3>
            <p className="py-4 text-sm">
              {t("Would you like to share the")}{" "}
              <strong>
                {langFlag} {langName}
              </strong>{" "}
              {t(
                "language package with all VBLive users? Anyone can then copy and build upon it."
              )}
            </p>
            <div className="modal-action flex gap-2">
              <button
                className="btn btn-ghost"
                onClick={() => handleSave(false)}
                disabled={saving}
              >
                {t("No, just save for me")}
              </button>
              <button
                className="btn btn-primary"
                onClick={() => handleSave(true)}
                disabled={saving}
              >
                {t("Yes, share it!")}
              </button>
              <button
                className="btn btn-outline"
                onClick={() => setSharePrompt(false)}
                disabled={saving}
              >
                {t("Cancel")}
              </button>
            </div>
          </div>
          <div
            className="modal-backdrop"
            onClick={() => !saving && setSharePrompt(false)}
          />
        </div>
      )}
    </div>
  );
}
